import {ActionReducerMapBuilder, createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {RootState} from "../../app/store";
import {SearchParams, LoginUsersForUserParams, UsersState} from "./UsersState";
import {list as listUsers, User, LoginUser, getLoginUsers} from "../../services/UsersService";
import {RejectError, RejectErrorType} from "../error/Error";
import {createDefaultMatchers, SliceStatus} from "../BaseState";
import {APIError} from "../../services/HttpService";
import {
  anonymizeLoginUser as anonymizeLoginUserAction,
  anonymizeUser as anonymizeUserAction,
  moveLoginUser as moveLoginUserAction,
  updateLoginUser as updateLoginUserAction
} from "../../services/UsersService";
import {close as closeAnonymizeLoginUserModal} from "../loginUsers/anonymizeLoginUserModal/AnonymizeLoginUserModalSlice";
import {close as closeAnonymizeUserModal} from "./user/anonymizeUserModal/AnonymizeUserModalSlice";
import {close as closeMoveLoginUserModal} from "../loginUsers/moveLoginUserModal/MoveLoginUserModalSlice";
import {get as getUser} from "./user/UserSlice";

const initialSearchParams = {
  page: 0,
  limit: 20,
  search: null
};

const initialState: UsersState = {
  users: [],
  searchParams: initialSearchParams,
  pendings: [],
  status: SliceStatus.idle,
  error: null,
  loginUsersForUsers: {}
};

export interface UpdateLoginUserData {
  readonly loginUser: {
    email: string;
  };
  readonly originalLoginUser: {
    id: number;
    userId: number;
  };
}

export const list = createAsyncThunk<Array<User>, SearchParams, {rejectValue: RejectError}>(
  "users/list",
  async ({page, limit, search}: SearchParams, {rejectWithValue}) => {
    try {
      const result = await listUsers(page * limit, limit, search);
      return result;
    } catch (error) {
      if (error instanceof APIError) return rejectWithValue({message: error.message, code: error.statusCode, type: RejectErrorType.API});

      throw error;
    }
  },
  {
    condition: (action: SearchParams, {getState}: {getState: RootState}): boolean => {
      const state = getState().users as UsersState;
      if (state.status === SliceStatus.loading) {
        return false;
      }

      return true;
    }
  }
);

export const listLoginUsers = createAsyncThunk<LoginUser[], LoginUsersForUserParams, {rejectValue: RejectError}>(
  "users/loginUsers",
  async ({userId}: LoginUsersForUserParams, {rejectWithValue}) => {
    try {
      const result = await getLoginUsers(userId);
      return result;
    } catch (error) {
      if (error instanceof APIError) return rejectWithValue({message: error.message, code: error.statusCode, type: RejectErrorType.API});

      throw error;
    }
  }
);

export const updateLoginUser = createAsyncThunk<LoginUser, UpdateLoginUserData, {rejectValue: RejectError}>(
  "users/updateLoginUser",
  async (update: UpdateLoginUserData, {rejectWithValue}) => {
    try {
      const result = await updateLoginUserAction(update.originalLoginUser.id, {email: update.loginUser.email});
      return result;
    } catch (error) {
      if (error instanceof APIError) return rejectWithValue({message: error.message, code: error.statusCode, type: RejectErrorType.API});
      throw error;
    }
  }
);

export const anonymizeLoginUser = createAsyncThunk<boolean, LoginUser>("loginUsers/delete", async (loginUser, thunkAPI) => {
  await anonymizeLoginUserAction(loginUser.id);
  thunkAPI.dispatch(closeAnonymizeLoginUserModal());
  return true;
});

export const anonymizeUser = createAsyncThunk<boolean, User>("user/delete", async (user, thunkAPI) => {
  await anonymizeUserAction(user.id);
  thunkAPI.dispatch(closeAnonymizeUserModal());
  thunkAPI.dispatch(getUser(user.id));
  return true;
});

export const moveLoginUser = createAsyncThunk<boolean, {loginUser: LoginUser; targetUser: User; role: LoginUser["role"]}>(
  "loginUsers/move",
  async (action, thunkAPI) => {
    await moveLoginUserAction(action.loginUser.id, action.targetUser.id, action.role);
    thunkAPI.dispatch(closeMoveLoginUserModal());
    return true;
  }
);

const createActions = (builder: ActionReducerMapBuilder<UsersState>) => {
  builder.addCase(list.fulfilled, (draft, action) => {
    draft.users = action.payload;
  });
  builder.addCase(listLoginUsers.fulfilled, (draft, action) => {
    draft.loginUsersForUsers[action.meta.arg.userId] = action.payload;
  });
  builder.addCase(anonymizeLoginUser.fulfilled, (draft, action) => {
    draft.loginUsersForUsers[action.meta.arg.userId] = draft.loginUsersForUsers[action.meta.arg.userId].filter(
      (loginUser) => loginUser.id !== action.meta.arg.id
    );
  });
  builder.addCase(moveLoginUser.fulfilled, (draft, action) => {
    draft.loginUsersForUsers[action.meta.arg.loginUser.userId] = draft.loginUsersForUsers[action.meta.arg.loginUser.userId].filter(
      (loginUser) => loginUser.id !== action.meta.arg.loginUser.id
    );
  });
  builder.addCase(updateLoginUser.fulfilled, (draft, action) => {
    draft.loginUsersForUsers[action.payload.userId] = draft.loginUsersForUsers[action.payload.userId].map((l) =>
      l.id === action.payload.id ? {...action.payload} : {...l}
    );
  });
  createDefaultMatchers(list.typePrefix, builder);
};

export const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    setSearchParams: (draft, action: PayloadAction<SearchParams>) => {
      draft.searchParams = action.payload;
      if (draft.searchParams.search !== action.payload.search) {
        draft.searchParams.page = 0;
      }
    }
  },
  extraReducers: createActions
});

export const usersState = (state: RootState): UsersState => state.users;
export const {setSearchParams} = usersSlice.actions;
export default usersSlice.reducer;
